import { number, route, string } from 'react-router-typesafe-routes'
import { BreadcrumbItem } from '~/components/Breadcrumb'

// We use the package `react-router-typesafe-routes` to re-define the
// file-based routes in `apps/routes/*` to achieve type safety.
// Since v7, react-router offers type-safe routes out of the box.
// We don't use this feature yet. This may change in the future.

// What is the difference between apps/routes.ts and apps/routes-typed.ts?
// `apps/routes.ts` is the enty point for the react-router.
// This `ROUTES` definition once lived in the `apps/routes.ts` file, which
// caused console errors.

export const ROUTES = {
  AUTH: {
    LOGIN: route({ path: '', searchParams: { error: string() } }),
    LOGOUT_SUCCESS: route({ path: 'auth/logout-success' }),
    PASSWORD_RESET: route({ path: 'auth/password-reset' }),
  },
  ACCOUNT: route({ path: 'account' }),
  SSO: route({
    path: 'sso',
    children: {
      CALLBACK: route({ path: 'callback' }),
    },
  }),
  API: route({
    path: 'api',
    children: {
      AUTH: route({
        path: 'auth',
        children: {
          LOGIN: route({ path: 'login' }),
          LOGOUT: route({ path: 'logout' }),
        },
      }),
      PAGE_BUILDER: route({
        path: 'page-builder',
        children: {
          FILE_UPLOAD: route({ path: 'file-upload' }),
        },
      }),
    },
  }),
  MS_AUTH: route({
    path: 'ms-auth',
    children: {
      CALLBACK: route({ path: 'callback' }),
    },
  }),
  GOOGLE_AUTH: route({
    path: 'google-auth',
    children: {
      CALLBACK: route({ path: 'callback' }),
    },
  }),
  GOOGLE_AUTH_BUSINESS_CALLBACK: route({
    path: 'google-auth-business-callback',
  }),
  DATA_PRIVACY: route({ path: 'data-privacy' }),
  START: route({ path: 'start' }),
  CHOOSE_SERVICE: route({ path: 'choose-service' }),
  CHOOSE_STORE: route({ path: 'choose-store' }),
  ERP_REDIRECT: route({ path: 'erp-sync' }),
  CONTRACT_MANAGEMENT: route({ path: 'contract-management' }),
  GOOGLE_BUSINESS_REDIRECT: route({
    path: 'google-business',
  }),
  PAGE_BUILDER: route({
    path: 'page-builder',
  }),
  INVENTORY: route({
    path: 'article-inventory',
  }),
  COMPANY: route({
    path: 'company/:companyId',
    params: { companyId: number() },
    children: {
      SETTINGS: route({
        path: 'settings',
        searchParams: { storeId: number() },
        children: {
          EMPLOYEES: route({ path: 'employees' }),
          USER_ABSENCES: route({ path: 'absence' }),
          STORES: route({ path: 'stores' }),
          DOMAINS: route({ path: 'domains' }),
        },
      }),
      ERP: route({ path: 'erp-sync' }),
      GOOGLE_BUSINESS: route({
        path: 'google-business',
        searchParams: { error: string() },
      }),
      PAGE_BUILDER: route({
        path: 'page-builder',
        children: {
          GENERAL: route({ path: 'general' }),
          COMPANY_PROFILE: route({ path: 'company-profile' }),
          NEWS_ARTICLES: route({
            path: 'news-articles',
            children: {
              ARTICLE: route({
                path: ':articleId',
                params: { articleId: number() },
                children: {
                  NEW: route({ path: 'new' }),
                },
              }),
              NEW_ARTICLE: route({ path: 'new' }),
            },
          }),
          IMPRINT: route({
            path: 'imprint',
          }),
        },
      }),
      INVENTORY: route({
        path: 'article-inventory',
        children: {
          SEARCH: route({ path: 'search' }),
          SUPPLIER: route({ path: 'supplier' }),
          FAQ: route({ path: 'faq' }),
        },
      }),
    },
  }),
  STORE: route({
    path: 'store/:id',
    params: { id: number() },
    children: {
      CALENDAR: route({
        path: 'calendar',
        searchParams: { employeeId: number() },
        children: {
          MY_APPOINTMENTS: route({ path: 'my-appointments' }),
        },
      }),
      NEW_APPOINTMENT: route({
        path: 'new-appointment',
        children: {
          RECIPIENT: route({ path: 'recipient' }),
          CONFIRM: route({ path: 'confirm' }),
        },
      }),
      APPOINTMENT_DETAILS: route({
        path: 'appointment/:appointmentId',
        params: { appointmentId: number() },
        children: {
          RESCHEDULE: route({
            path: 'reschedule',
            children: {
              CONFIRM: route({ path: 'confirm' }),
            },
          }),
        },
      }),
      ONBOARDING_SUCCESS: route({ path: 'onboarding-success' }),
      STORE_SETTINGS: route({
        path: 'store-settings',
        children: {
          BASIC: route({ path: 'basic-info' }),
          SERVICE_TYPES: route({ path: 'services' }),
          EMPLOYEES: route({
            path: 'employees',
            searchParams: { highlightUserId: number() },
          }),
          ROOMS: route({ path: 'rooms' }),
          GOOGLE_MAPS: route({ path: 'location' }),
          UNAVAILABLE_SERVICES: route({
            path: 'unavailable-services',
          }),
        },
      }),
    },
  }),
  EMPLOYEE_SETTINGS: route({
    path: 'employee-settings',
    children: {
      MY_WORKING_HOURS: route({
        path: 'my-working-hours',
        searchParams: {
          storeId: number(),
          next: string(),
        },
      }),
    },
  }),
  CALENDAR_SETTINGS: route({
    path: 'calendar-settings',
    children: {
      CALENDAR_SYNC: route({
        path: 'sync',
        searchParams: {
          error: string(),
          deletedSessionsCount: number(),
          storeId: number(),
        },
      }),
    },
  }),
  ONBOARDING: route({
    path: 'onboarding/:storeId',
    params: { storeId: number() },
    children: {
      WELCOME: route({ path: 'welcome' }),
      BASIC_INFO: route({ path: 'basic-info' }),
      SERVICES: route({ path: 'services' }),
      EMPLOYEES: route({ path: 'employees' }),
      ROOMS: route({ path: 'rooms' }),
      GOOGLE_MAPS: route({ path: 'location' }),
    },
  }),
}

export const HOME_BREADCRUMB_ITEM: BreadcrumbItem = {
  label: 'Startseite',
  to: ROUTES.START.$path(),
}
